import React, { memo, useMemo, useRef } from "react";
import { useImmer } from "use-immer";

import {
  checkoutUpsellRevenueGraphConfig,
  postPurchaseRevenueGraphConfig,
} from "@intelligems/ig-graphs";
import {
  AnalyzeTinybirdResourceResponseBodyType,
  CustomEventDto,
  GraphConfig,
} from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { useSetGridWidthAnalytics } from "@src/features/analytics/hooks/use-set-grid-width-analytics";
import { MetricDetailsWrapper } from "@src/features/analytics/metricDetails/MetricDetailsWrapper";
import { AnalyticsAudienceTableWrapper } from "@src/features/analytics/tables/analyticsAudienceTable/AnalyticsAudienceTableWrapper";
import { getGridWidthCalc } from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { getHasAnyUpsellBlocks } from "@src/store/slices/checkoutBlocks/selectors";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

interface RevenueAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}

export const RevenueAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    graphConfigs,
    testDuration,
    compareVariationId,
  }: RevenueAnalyticsPageProps) => {
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);
    const hasUpsellBlocks = useSelector(getHasAnyUpsellBlocks());
    const hasPostPurchaseUpsell = experience.testTypes.hasTestPostPurchase;
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");

    const [hover, setHover] = useImmer(-1);

    const ref = useRef<HTMLDivElement | null>(null);
    const gridWidth = useSetGridWidthAnalytics(ref);

    return (
      <Stack ref={ref} direction="column" spacing={4}>
        <Grid>
          <BaseMetricSection
            groupTitle={"Revenue Metrics"}
            showCompareGroups={true}
          >
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(0);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  totalTestVisitors={totalExperienceVisitors}
                  config={graphConfigs[0]}
                  renderLocation={"experiment"}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[0],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(1);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  config={graphConfigs[1]}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  totalTestVisitors={totalExperienceVisitors}
                  renderLocation={"experiment"}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[1],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(2);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  testDuration={testDuration}
                  config={graphConfigs[2]}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  renderLocation={"experiment"}
                  totalTestVisitors={totalExperienceVisitors}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[2],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(3);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[3]}
                  testDuration={testDuration}
                  renderLocation={"experiment"}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  totalTestVisitors={totalExperienceVisitors}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[3],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(3);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[4]}
                  testDuration={testDuration}
                  renderLocation={"experiment"}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  totalTestVisitors={totalExperienceVisitors}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[4],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(5);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[5]}
                  testDuration={testDuration}
                  renderLocation={"experiment"}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  totalTestVisitors={totalExperienceVisitors}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[5],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            {hasUpsellBlocks && (
              <Grid size={gridWidth}>
                <BaseGraphPaper
                  onMouseEnter={() => {
                    setHover(6);
                  }}
                  onMouseLeave={() => {
                    setHover(-1);
                  }}
                >
                  <GraphsWrapper
                    config={checkoutUpsellRevenueGraphConfig[0]}
                    testDuration={testDuration}
                    renderLocation={"experiment"}
                    isDataLoading={data[0].isLoading || data[0].isPending}
                    totalTestVisitors={totalExperienceVisitors}
                    data={selectData({
                      dashboardData: data,
                      config: checkoutUpsellRevenueGraphConfig[0],
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex: 0,
                      variations,
                    })}
                    rawData={data}
                    compareVariationId={compareVariationId}
                  />
                </BaseGraphPaper>
              </Grid>
            )}
            {hasPostPurchaseUpsell && (
              <Grid size={gridWidth}>
                <BaseGraphPaper
                  onMouseEnter={() => {
                    setHover(hasUpsellBlocks ? 7 : 6);
                  }}
                  onMouseLeave={() => {
                    setHover(-1);
                  }}
                >
                  <GraphsWrapper
                    config={postPurchaseRevenueGraphConfig[0]}
                    testDuration={testDuration}
                    renderLocation={"experiment"}
                    isDataLoading={data[0].isLoading || data[0].isPending}
                    totalTestVisitors={totalExperienceVisitors}
                    data={selectData({
                      dashboardData: data,
                      config: postPurchaseRevenueGraphConfig[0],
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex: 0,
                      variations,
                    })}
                    rawData={data}
                    compareVariationId={compareVariationId}
                  />
                </BaseGraphPaper>
              </Grid>
            )}
          </BaseMetricSection>
        </Grid>
        <Grid>
          <MetricDetailsWrapper
            graphConfigs={[
              graphConfigs[0],
              graphConfigs[4],
              graphConfigs[2],
              graphConfigs[3],
            ]}
            dropdownOptions={[
              graphConfigs[0],
              graphConfigs[4],
              graphConfigs[2],
              graphConfigs[3],
            ].map((config) => ({
              title: config.title[0],
              id: config.metrics[0],
            }))}
            variationOverviewData={data}
          />
        </Grid>
        <Grid>
          <AnalyticsAudienceTableWrapper
            experience={experience}
            variations={variations}
            dashboardData={data}
            testDuration={testDuration}
            graphConfigs={[
              graphConfigs[0],
              graphConfigs[4],
              graphConfigs[2],
              graphConfigs[3],
            ]}
          />
        </Grid>
      </Stack>
    );
  }
);
